import type {
  ApiKeyPatch, ApiKeySecret, AvailabilityView, CalendarConnect, CalendarConnectionView, CalendarProvidersView, ConnectorDraftInput, ConnectorInput,
  ConnectorRunView, ConnectorTestInput, ConnectorTestResult, ConnectorView, EventKind, ServiceAccountCreate, ServiceAccountView, WebhookDeliveryView,
  WebhookHealthView, WebhookPatch,
} from '@takeit4me/shared'
import type { Http } from '../http'

type In<T extends { _zod: { input: unknown } }> = T['_zod']['input']
export type ServiceAccountBody = In<typeof ServiceAccountCreate>
export type ApiKeyPatchBody = In<typeof ApiKeyPatch>
export type WebhookPatchBody = In<typeof WebhookPatch>
export type ConnectorBody = In<typeof ConnectorInput>
export type ConnectorTestBody = In<typeof ConnectorTestInput>
export type ConnectorDraftBody = In<typeof ConnectorDraftInput>
export type CalendarConnectBody = In<typeof CalendarConnect>
export type ConnectorSaved = ConnectorView & { signing_secret?: string | null; paused?: boolean }
export interface ConnectorDraftResult { connector: ConnectorView; test: ConnectorTestResult; questions: string[]; needs_secrets: string[] }

/** Console API methods: workstream F (API keys, outbound calls and webhooks, calendars). */
export interface ConnectorsApi {
  serviceAccounts(): Promise<ServiceAccountView[]>
  /** A service account with its first key; the secret is in the answer and never again. */
  createServiceAccount(body: ServiceAccountBody): Promise<ApiKeySecret>
  updateApiKey(id: string, body: ApiKeyPatchBody): Promise<ServiceAccountView>
  /** A new key with the same scopes; the old one works for `graceHours` more (0: stops at once). */
  rotateApiKey(id: string, graceHours: number): Promise<ApiKeySecret>
  revokeApiKey(id: string): Promise<{ ok: true }>
  disableServiceAccount(id: string): Promise<{ ok: true }>

  webhookEvents(): Promise<{ catalogue: EventKind[]; seen: string[] }>
  /** Webhooks with their health (failures in a row, pending, failed today). */
  webhookHealth(): Promise<WebhookHealthView[]>
  updateWebhook(id: string, body: WebhookPatchBody): Promise<{ ok: true }>
  webhookDeliveries(id: string, q?: { before?: string; limit?: number }): Promise<WebhookDeliveryView[]>
  resendWebhookDelivery(id: string, deliveryId: string): Promise<WebhookDeliveryView>
  testWebhook(id: string): Promise<WebhookDeliveryView>

  connectors(): Promise<ConnectorView[]>
  createConnector(body: ConnectorBody): Promise<ConnectorSaved>
  updateConnector(id: string, body: ConnectorBody): Promise<ConnectorSaved>
  deleteConnector(id: string): Promise<{ ok: true }>
  testConnector(id: string, body?: ConnectorTestBody): Promise<ConnectorTestResult>
  /** Switches a tested connector on (an admin's approval). */
  connectorLive(id: string): Promise<ConnectorView>
  pauseConnector(id: string, reason?: string): Promise<ConnectorView>
  connectorRuns(id: string, q?: { before?: string; limit?: number }): Promise<ConnectorRunView[]>
  resendConnectorRun(id: string, runId: string): Promise<ConnectorRunView | null>
  /** The AI drafts a connector from a request; it stays a draft until an admin switches it on. */
  draftConnector(body: ConnectorDraftBody): Promise<ConnectorDraftResult>

  calendars(): Promise<{ providers: CalendarProvidersView; connections: CalendarConnectionView[] }>
  /** Starts a calendar connection; open `authorize_url` to sign in with the provider. */
  connectCalendar(body: CalendarConnectBody): Promise<{ connection: CalendarConnectionView; authorize_url: string }>
  disconnectCalendar(id: string): Promise<{ ok: true }>
  availability(q: { from: string; to: string; slot_minutes?: number; connection_id?: string; staff_id?: string }): Promise<AvailabilityView>
}

const enc = encodeURIComponent

export const connectorsMethods = (http: Http): ConnectorsApi => ({
  serviceAccounts: () => http.get<ServiceAccountView[]>('/api-keys'),
  createServiceAccount: (body) => http.post<ApiKeySecret>('/api-keys', body),
  updateApiKey: (id, body) => http.patch<ServiceAccountView>(`/api-keys/${enc(id)}`, body),
  rotateApiKey: (id, graceHours) => http.post<ApiKeySecret>(`/api-keys/${enc(id)}/rotate`, { grace_hours: graceHours }),
  revokeApiKey: (id) => http.post<{ ok: true }>(`/api-keys/${enc(id)}/revoke`, {}),
  disableServiceAccount: (id) => http.post<{ ok: true }>(`/service-accounts/${enc(id)}/disable`, {}),

  webhookEvents: () => http.get<{ catalogue: EventKind[]; seen: string[] }>('/webhooks/events'),
  webhookHealth: () => http.get<WebhookHealthView[]>('/webhooks'),
  updateWebhook: (id, body) => http.patch<{ ok: true }>(`/webhooks/${enc(id)}`, body),
  webhookDeliveries: (id, q = {}) => http.get<WebhookDeliveryView[]>(`/webhooks/${enc(id)}/deliveries`, q),
  resendWebhookDelivery: (id, did) => http.post<WebhookDeliveryView>(`/webhooks/${enc(id)}/deliveries/${enc(did)}/resend`, {}),
  testWebhook: (id) => http.post<WebhookDeliveryView>(`/webhooks/${enc(id)}/test`, {}),

  connectors: () => http.get<ConnectorView[]>('/connectors'),
  createConnector: (body) => http.post<ConnectorSaved>('/connectors', body),
  updateConnector: (id, body) => http.put<ConnectorSaved>(`/connectors/${enc(id)}`, body),
  deleteConnector: (id) => http.delete<{ ok: true }>(`/connectors/${enc(id)}`),
  testConnector: (id, body = {}) => http.post<ConnectorTestResult>(`/connectors/${enc(id)}/test`, body),
  connectorLive: (id) => http.post<ConnectorView>(`/connectors/${enc(id)}/live`, {}),
  pauseConnector: (id, reason) => http.post<ConnectorView>(`/connectors/${enc(id)}/pause`, { reason: reason ?? null }),
  connectorRuns: (id, q = {}) => http.get<ConnectorRunView[]>(`/connectors/${enc(id)}/runs`, q),
  resendConnectorRun: (id, rid) => http.post<ConnectorRunView | null>(`/connectors/${enc(id)}/runs/${enc(rid)}/resend`, {}),
  draftConnector: (body) => http.post<ConnectorDraftResult>('/connectors/draft', body),

  calendars: () => http.get<{ providers: CalendarProvidersView; connections: CalendarConnectionView[] }>('/calendars'),
  connectCalendar: (body) => http.post<{ connection: CalendarConnectionView; authorize_url: string }>('/calendars/connect', body),
  disconnectCalendar: (id) => http.delete<{ ok: true }>(`/calendars/${enc(id)}`),
  availability: (q) => http.get<AvailabilityView>('/calendars/availability', q),
})
